import {
  CarrierVisitStatus,
  ContainerJourneyResponseDto,
  ContainerPositionType,
  OperationType,
  OrderStatus,
} from '@planning/app/api'
import { ContainerJourneyStatus } from '@planning/components/molecules/ContainerJourneyStatusChip'
import { mapToJourneyHoldType } from '@planning/pages/Holds/models/hold.model'
import { ContainerVisitStatus } from '@planning/pages/Order/components/OrdersListCard'
import { EntityItem } from '@planning/rt-stores/base/EntityItem'
import { IContainerItem } from '@planning/rt-stores/container/ContainerItem'
import { ContainerItemStore } from '@planning/rt-stores/container/ContainerItemStore'
import { HoldItemStore } from '@planning/rt-stores/hold/HoldItemStore'
import { IOrderItem } from '@planning/rt-stores/order/OrderItem'
import { OrderItemStore } from '@planning/rt-stores/order/OrderItemStore'
import { IEntity } from '@planning/rt-stores/types'
import _ from 'lodash'
import { computed, makeObservable } from 'mobx'
import { ContainerJourneyItemStore } from './ContainerJourneyItemStore'

export interface IContainerJourneyItem extends IEntity<ContainerJourneyResponseDto> {
  status: ContainerJourneyStatus
  container?: IContainerItem
  inboundOrder?: IOrderItem
  inboundOrderStatus?: OrderStatus
  inboundCarrierVisitStatus?: CarrierVisitStatus
  outboundOrder?: IOrderItem
  outboundOrderStatus?: OrderStatus
  outboundCarrierVisitStatus?: CarrierVisitStatus
  isOnTerminal: boolean
  canUnassignFromRelease: boolean
}

export class ContainerJourneyItem
  extends EntityItem<ContainerJourneyResponseDto>
  implements IContainerJourneyItem
{
  constructor(
    public containerJourneyItemStore: ContainerJourneyItemStore,
    id: number,
    data: ContainerJourneyResponseDto,
    public orderItemStore?: OrderItemStore,
    public containerItemStore?: ContainerItemStore,
    public holdItemStore?: HoldItemStore,
  ) {
    super(id, data)

    makeObservable(this, {
      status: computed,
      container: computed,
      inboundOrder: computed,
      inboundOrderStatus: computed,
      inboundCarrierVisitStatus: computed,
      outboundOrder: computed,
      outboundOrderStatus: computed,
      outboundCarrierVisitStatus: computed,
      isOnTerminal: computed,
      canUnassignFromRelease: computed,
      holds: computed,
    })
  }

  get container() {
    return this.containerItemStore?.elements[this.data.containerId]
  }

  get isOnTerminal() {
    return this.inboundOrderStatus === OrderStatus.Fulfilled
  }

  get status(): ContainerJourneyStatus {
    // todo: consolidate with ContainerJourneyDataStore.getContainerJourneyStatus
    if (
      this.outboundCarrierVisitStatus === CarrierVisitStatus.Departed ||
      this.outboundOrderStatus === OrderStatus.Fulfilled
    )
      return ContainerJourneyStatus.Departed

    if (this.data.currentPosition === ContainerPositionType.Equipment)
      return ContainerJourneyStatus.OnTerminal

    if (this.isOnTerminal && this.data.currentPosition === ContainerPositionType.Yard)
      return ContainerJourneyStatus.OnYard

    if (this.isOnTerminal) return ContainerJourneyStatus.OnTerminal

    if (
      this.inboundOrder?.visit &&
      (this.inboundCarrierVisitStatus === CarrierVisitStatus.Arrived ||
        this.inboundOrder?.visit?.data.status !== CarrierVisitStatus.Expected)
    )
      return ContainerJourneyStatus.Arrived

    return ContainerVisitStatus.Expected
  }

  get inboundOrder() {
    return this.orderItemStore?.elements[this.data.inboundOrderId]
  }

  get inboundOrderStatus() {
    return this.inboundOrder?.data.status
  }

  get inboundCarrierVisitStatus() {
    return this.inboundOrder?.visit?.data.status
  }

  get outboundOrder() {
    return this.orderItemStore?.elements[this.data.outboundOrderId ?? -1]
  }

  get outboundOrderStatus() {
    return this.outboundOrder?.data.status
  }

  get outboundCarrierVisitStatus() {
    return this.outboundOrder?.visit?.data.status
  }

  get canUnassignFromRelease() {
    const validJourneyStatus = [ContainerJourneyStatus.OnYard]

    if (this.outboundOrderStatus === OrderStatus.Fulfilled) return false

    if (
      this.data.operationType === OperationType.Inbound ||
      this.data.operationType === OperationType.Internal
    )
      return true

    return validJourneyStatus.includes(this.status)
  }

  get holds() {
    return _(this.holdItemStore?.elements)
      .filter(hold => hold.data.containerJourneyId === this.id)
      .map(mapToJourneyHoldType)
  }
}
